import React, { useState, useContext, useEffect } from 'react';
import { useParams, useLocation, Navigate, Link } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { getPropertyTypeLabel } from '../utils/propertyUtils';
import { getPropertyById } from '../services/propertyService';
import PageWrapper from '../components/PageWrapper';
import PropertyOverview from '../components/PropertyOverview';
import PropertyListings from '../components/PropertyListings';
import {
  HomeIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  ListBulletIcon,
  InformationCircleIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const PropertyDetails = () => {
  const { agencyCurrency } = useContext(AuthContext);
  // Property state - core responsibility
  const [property, setProperty] = useState(null);
  const [propertyLoading, setPropertyLoading] = useState(true);
  const [propertyError, setPropertyError] = useState(null);

  // Main tab state
  const [mainTab, setMainTab] = useState('listings');

  const { id } = useParams();
  const { search } = useLocation();
  const initialListingId = new URLSearchParams(search).get('listingId')
    ? Number(new URLSearchParams(search).get('listingId'))
    : null;

  // Fetch property details - core responsibility
  useEffect(() => {
    const fetchPropertyDetails = async () => {
      try {
        setPropertyLoading(true);
        setPropertyError(null);

        const agencyId = sessionStorage.getItem('agencyId');
        if (!agencyId) {
          throw new Error('No agency ID found in session storage');
        }

        const propertyId = parseInt(id);
        if (isNaN(propertyId)) {
          throw new Error('Invalid property ID');
        }

        console.log('[PropertyDetails] Fetching property details for:', { agencyId, propertyId });
        const propertyData = await getPropertyById(agencyId, propertyId);
        setProperty(propertyData);

      } catch (error) {
        console.error('[PropertyDetails] Error fetching property:', error);
        setPropertyError(error.message);
      } finally {
        setPropertyLoading(false);
      }
    };

    if (id) {
      fetchPropertyDetails();
    }
  }, [id]);

  // Refresh property when photos change (cover image may have updated)
  const handlePhotoChange = async () => {
    try {
      const agencyId = sessionStorage.getItem('agencyId');
      if (!agencyId) return;
      const propertyData = await getPropertyById(agencyId, parseInt(id));
      setProperty(propertyData);
    } catch (err) {
      console.error('[PropertyDetails] Failed to refresh property after photo change:', err);
    }
  };

  if (propertyLoading) {
    return (
      <PageWrapper>
        <div className="min-h-screen bg-gray-50 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Loading property details...</h3>
              <p className="mt-2 text-gray-500">Please wait while we fetch the property information</p>
            </div>
          </div>
        </div>
      </PageWrapper>
    );
  }

  if (propertyError) {
    return (
      <PageWrapper>
        <div className="min-h-screen bg-gray-50 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center py-20">
              <div className="bg-red-50 border border-red-200 rounded-xl p-8 max-w-md mx-auto">
                <div className="text-red-400 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-red-900 mb-2">Error Loading Property</h3>
                <p className="text-red-700">{propertyError}</p>
              </div>
            </div>
          </div>
        </div>
      </PageWrapper>
    );
  }

  if (!property) {
    return <Navigate to="/properties" />;
  }

  return (
    <PageWrapper>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2 text-sm">
              <li>
                <Link
                  to="/properties"
                  className="flex items-center text-gray-500 hover:text-emerald-600 transition-colors duration-200"
                >
                  <HomeIcon className="w-4 h-4 mr-1" />
                  Properties
                </Link>
              </li>
              <li>
                <ChevronRightIcon className="w-4 h-4 text-gray-400" />
              </li>
              <li className="text-gray-900 font-medium">{property.name}</li>
            </ol>
          </nav>

          {/* Property Header */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-6">
              <div className="flex items-center justify-between text-white">
                <div>
                  <div className="flex items-center mb-2">
                    <BuildingOfficeIcon className="w-6 h-6 mr-3" />
                    <h1 className="text-2xl font-bold">{property.name}</h1>
                    <span className="ml-3 inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-white/20 text-white">
                      {getPropertyTypeLabel(property.property_type)}
                    </span>
                  </div>
                  <div className="flex items-center text-emerald-100">
                    <MapPinIcon className="w-4 h-4 mr-2" />
                    <span>{property.address}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Tabs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
            <div className="flex border-b border-gray-200">
              <button
                className={`flex items-center px-6 py-4 text-sm font-medium transition-colors duration-200 border-b-2 ${
                  mainTab === 'listings'
                    ? 'border-emerald-500 text-emerald-600 bg-emerald-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setMainTab('listings')}
              >
                <ListBulletIcon className="w-4 h-4 mr-2" />
                Rental Details
              </button>
              <button
                className={`flex items-center px-6 py-4 text-sm font-medium transition-colors duration-200 border-b-2 ${
                  mainTab === 'details'
                    ? 'border-emerald-500 text-emerald-600 bg-emerald-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setMainTab('details')}
              >
                <InformationCircleIcon className="w-4 h-4 mr-2" />
                Property Details
              </button>
            </div>
          </div>

          {/* Main Tab Content */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {mainTab === 'details' && (
              <PropertyOverview
                property={property}
                agencyCurrency={agencyCurrency}
                onPhotoChange={handlePhotoChange}
              />
            )}
            {mainTab === 'listings' && (
              <PropertyListings
                property={property}
                agencyCurrency={agencyCurrency}
                initialListingId={initialListingId}
              />
            )}
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default PropertyDetails;
